import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLandingPageSettings } from "../../api/getLandingPageSettings";
import { LEAD_API } from "../../api/apiConfig";
import { hasConsent, trackClick } from "../../utils/loadLandingTracking";
import "./Hero.css";

// Identify the user via TeleforceCDP after a successful lead submission,
// using the CDP library's own hashEmail/hashPhone so hashing stays
// consistent with what the server expects. Guarded by consent.
const identifyWithCDP = async (email, phone) => {
  if (!hasConsent()) return;

  const cdp = window.TeleforceCDP;
  if (!cdp || !cdp._initialized) return;

  try {
    const normEmail = email.trim().toLowerCase();
    const normPhone = phone.replace(/\D/g, "");

    const [emailHash, phoneHash] = await Promise.all([
      normEmail ? cdp.hashEmail(normEmail) : Promise.resolve(null),
      normPhone ? cdp.hashPhone(normPhone) : Promise.resolve(null),
    ]);

    if (emailHash || phoneHash) {
      cdp.identify(emailHash, phoneHash);
    }
  } catch (err) {
    console.warn("[TeleforceCDP] identify failed:", err);
  }
};

const Hero = () => {
  const navigate = useNavigate();

  const nameRegex = /^[A-Za-z\s]+$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const mobileRegex = /^[0-9]{10}$/;

  const [accountId, setAccountId] = useState("");
  const [source, setSource] = useState("");
  const [errors, setErrors] = useState({});
  const [highlightForm, setHighlightForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [hero, setHero] = useState({
    title: "Best Live Chat Software",
    h1_1: "",
    h1_2: "",
    h1_3: "",
    description: "",
  });

  const [formPlaceholders, setFormPlaceholders] = useState({
    company: "Company",
    users: "Number Of Users",
    previousSoftware: "Which software were using in the past?",
  });

  const [partnerLogos, setPartnerLogos] = useState([]);
  const [companyName, setCompanyName] = useState("Teleforce");

  const [liveChat, setLiveChat] = useState({
    title: "",
    description: "",
    extended: "",
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    countryCode: "+91",
    mobile: "",
    company: "",
    users: "",
    previousSoftware: "",
  });

  useEffect(() => {
    const fetchSettings = async () => {
      const data = await getLandingPageSettings();
      if (!data) return;

      setAccountId(data.account_id || "");
      setSource(data.domain_name || "landing_page");

      setHero({
        title: data.hero_title || "Best Live Chat Software",
        h1_1: data.hero_highlight_1 || "",
        h1_2: data.hero_highlight_2 || "",
        h1_3: data.hero_highlight_3 || "",
        description: data.hero_description
          ? data.hero_description.replace(/â/g, "—")
          : "",
      });

      setFormPlaceholders({
        company: data.lead_form_company || "Company",
        users: data.lead_form_users || "Number Of Users",
        previousSoftware:
          data.lead_form_previous_software ||
          "Which software were using in the past?",
      });

      if (Array.isArray(data.company_partner_logos)) {
        setPartnerLogos(data.company_partner_logos);
      }

      setCompanyName(data.company_name || "Teleforce");

      setLiveChat({
        title: data.live_chat_title || "",
        description: data.live_chat_description || "",
        extended: data.live_chat_extended_description || "",
      });
    };

    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => {
      const err = { ...prev };

      if (name === "name") {
        if (!value.trim()) err.name = "Name is required";
        else if (!nameRegex.test(value))
          err.name = "Name should contain only alphabets";
        else delete err.name;
      }

      if (name === "email") {
        if (!value.trim()) err.email = "Email is required";
        else if (!emailRegex.test(value))
          err.email = "Enter a valid email address";
        else delete err.email;
      }

      if (name === "mobile") {
        if (!value.trim()) err.mobile = "Mobile number is required";
        else if (!mobileRegex.test(value))
          err.mobile = "Enter a valid 10-digit mobile number";
        else delete err.mobile;
      }

      return err;
    });
  };

  const isFormValid =
    nameRegex.test(form.name) &&
    emailRegex.test(form.email) &&
    mobileRegex.test(form.mobile);

  const submitLead = async () => {
    if (!isFormValid || loading) return;

    setLoading(true);

    try {
      const res = await fetch(`${LEAD_API}/createlead/${accountId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          mobile: `${form.countryCode}${form.mobile}`,
          city: form.company || "N/A",
          address: form.users || "N/A",
          otherparams: [
            {
              meta_key: formPlaceholders.company,
              meta_value: form.company || "",
            },
            {
              meta_key: formPlaceholders.users,
              meta_value: form.users || "",
            },
            {
              meta_key: formPlaceholders.previousSoftware,
              meta_value: form.previousSoftware || "",
            },
            {
              meta_key: "source",
              meta_value: source,
            },
            {
              meta_key: "cdp_user_id",
              meta_value: window.TeleforceCDP?.getCdpUserId() || "",
            },
          ],
        }),
      });

      if (res.ok) {
        await identifyWithCDP(form.email, `${form.countryCode}${form.mobile}`);

        setForm({
          name: "",
          email: "",
          countryCode: "+91",
          mobile: "",
          company: "",
          users: "",
          previousSoftware: "",
        });
        setErrors({});
        navigate("/thank-you");
      } else {
        alert("Failed to submit lead");
      }
    } catch {
      alert("Server error");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    trackClick("hero-book-demo-btn");
    const el = document.getElementById("demo-form");
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightForm(true);
    setTimeout(() => setHighlightForm(false), 2000);
  };

  return (
    <section className="hero-wrapper">
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-7">
            <span className="hero-badge">
              <i className="bi bi-stars text-primary"></i> {hero.title}
            </span>

            <h1 className="hero-heading">
              {hero.h1_1}
              <br />
              {hero.h1_2}
              <br />
              {hero.h1_3}
            </h1>

            {hero.description && (
              <p className="hero-description">{hero.description}</p>
            )}

            <button className="btn btn-primary hero-cta" onClick={scrollToForm}>
              Book A Demo →
            </button>

            {partnerLogos.length > 0 && (
              <div className="hero-trust">
                {partnerLogos.map((logo, i) => (
                  <img key={i} src={logo} alt="partner" />
                ))}
              </div>
            )}
          </div>

          <div className="col-lg-5 d-flex justify-content-end">
            <div
              id="demo-form"
              className={`hero-form-card hero-form-narrow ${
                highlightForm ? "form-highlight" : ""
              }`}
            >
              <h4>
                Elevate your business communication
                <br />
                with {companyName} today. Try Now!
              </h4>

              <input
                className="form-control"
                placeholder="Your Name"
                name="name"
                value={form.name}
                onChange={handleChange}
              />
              {errors.name && (
                <small className="text-danger">{errors.name}</small>
              )}

              <input
                className="form-control"
                placeholder="Business Email*"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
              {errors.email && (
                <small className="text-danger">{errors.email}</small>
              )}

              <div className="d-flex gap-2">
                <select
                  className="form-select country-code"
                  name="countryCode"
                  value={form.countryCode}
                  onChange={handleChange}
                >
                  <option value="+91">+91 (India)</option>{" "}
                  <option value="+1">+1 (USA)</option>{" "}
                  <option value="+44">+44 (UK)</option>{" "}
                  <option value="+61">+61 (Australia)</option>{" "}
                  <option value="+971">+971 (UAE)</option>{" "}
                  <option value="+81">+81 (Japan)</option>{" "}
                  <option value="+65">+65 (Singapore)</option>{" "}
                  <option value="+49">+49 (Germany)</option>{" "}
                  <option value="+33">+33 (France)</option>{" "}
                  <option value="+39">+39 (Italy)</option>{" "}
                  <option value="+34">+34 (Spain)</option>{" "}
                  <option value="+7">+7 (Russia)</option>{" "}
                  <option value="+86">+86 (China)</option>{" "}
                  <option value="+82">+82 (South Korea)</option>{" "}
                  <option value="+92">+92 (Pakistan)</option>{" "}
                  <option value="+880">+880 (Bangladesh)</option>{" "}
                  <option value="+94">+94 (Sri Lanka)</option>{" "}
                  <option value="+60">+60 (Malaysia)</option>{" "}
                  <option value="+62">+62 (Indonesia)</option>{" "}
                  <option value="+63">+63 (Philippines)</option>{" "}
                  <option value="+27">+27 (South Africa)</option>{" "}
                  <option value="+234">+234 (Nigeria)</option>{" "}
                  <option value="+20">+20 (Egypt)</option>{" "}
                  <option value="+55">+55 (Brazil)</option>{" "}
                  <option value="+52">+52 (Mexico)</option>{" "}
                  <option value="+54">+54 (Argentina)</option>{" "}
                  <option value="+56">+56 (Chile)</option>
                </select>

                <input
                  className="form-control"
                  placeholder="Phone Number*"
                  name="mobile"
                  value={form.mobile}
                  maxLength={10}
                  onChange={(e) =>
                    /^[0-9]*$/.test(e.target.value) && handleChange(e)
                  }
                />
              </div>
              {errors.mobile && (
                <small className="text-danger">{errors.mobile}</small>
              )}

              <input
                className="form-control"
                placeholder={formPlaceholders.company}
                name="company"
                value={form.company}
                onChange={handleChange}
              />

              <input
                className="form-control"
                placeholder={formPlaceholders.users}
                name="users"
                value={form.users}
                onChange={handleChange}
              />

              <input
                className="form-control"
                placeholder={formPlaceholders.previousSoftware}
                name="previousSoftware"
                value={form.previousSoftware}
                onChange={handleChange}
              />

              <button
                className="btn btn-primary w-100 submit-btn"
                disabled={!isFormValid || loading}
                onClick={submitLead}
              >
                {loading ? "Submitting..." : "Start your free trial"}
              </button>
            </div>
          </div>
        </div>

        {liveChat.title && (
          <div className="row mt-5 pt-4">
            <div
              className="col-12 text-center mx-auto"
              style={{ maxWidth: 900 }}
            >
              <h2 className="fw-bold mb-3">{liveChat.title}</h2>
              <p className="text-muted fs-6 lh-lg">
                {liveChat.description}
                <br />
                <br />
                {liveChat.extended}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
